import Select from "common/Select/Select";
import React, { CSSProperties, useEffect } from "react";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import IconWithFallback from "Global/IconWithFallback";
import useFiltersQuery from "common/Filters/common/useFiltersQuery";
import { FiltersActions, FiltersSelector, WorkspaceType } from "@shared/types";
import { useLocation } from "react-router-dom";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useSelector } from "react-redux";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { useAdminGroupsSelect } from "features/admin/hooks";
import useMedia from "utils/hooks/useMedia";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { SizeType } from "antd/es/config-provider/SizeContext";

import { StyledSelect } from "common/Filters/common/styles.ts";

interface GroupSelectProps {
  filtersSelectors?: FiltersSelector;
  filtersActions?: FiltersActions;
  style?: CSSProperties;
  size?: SizeType;
}

const GroupSelect = ({
  filtersSelectors = groupConfigurationsFilters.selectors,
  filtersActions = groupConfigurationsFilters.actions,
  style,
  size,
}: GroupSelectProps) => {
  const location = useLocation();
  useFiltersQuery(filtersSelectors, filtersActions);
  const { updateUrl, groupsFilterSectionData, querySubGroupId } =
    useAdminGroupsSelect({
      filtersSelectors,
      filtersActions,
    });
  const maxDropdownWidth = useMedia([`(max-width: 400px)`], [300], 375);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const [filtersSubGroupId] = groupsFilterSectionData.value;
  const workspace = useSelector(selectWorkspace);
  const isAdmin = workspace === WorkspaceType.ADMIN;
  const options = groupsFilterSectionData?.options as unknown as
    | number[]
    | undefined;
  const showValue = !!options?.find((option) => option === filtersSubGroupId);

  useEffect(() => {
    if (filtersSubGroupId && isAdmin && !querySubGroupId) {
      updateUrl(filtersSubGroupId);
    }
  }, [location.pathname, filtersSubGroupId, isAdmin]);

  const getGroupFilterItem = (option: number) => {
    const imageSize = 28;
    return (
      <Flex
        $justifyContent="flex-start"
        style={{ marginRight: isBottomNavigator ? 0 : 18 }}
      >
        <Flex
          style={{
            height: imageSize,
            width: imageSize,
            marginLeft: isBottomNavigator ? 4 : 0,
            marginRight: isBottomNavigator ? 0 : 8,
            flex: "none",
          }}
        >
          <IconWithFallback
            {...groupsFilterSectionData.displayData?.getIconProps?.(option)}
            iconSize={isBottomNavigator ? "l" : "xxl"}
            iconStyle={{
              borderRadius: 4,
            }}
          />
        </Flex>
        <Text $ellipsis={true}>
          {groupsFilterSectionData.displayData?.getDisplay?.(option)}
        </Text>
      </Flex>
    );
  };

  const parsedOptions = options?.map((option) => {
    return {
      value: option,
      searchValue: groupsFilterSectionData.displayData?.getDisplay?.(option),
      label: getGroupFilterItem(option),
    };
  });

  return (
    <StyledSelect
      style={{ maxWidth: 350, ...style }}
      value={showValue ? filtersSubGroupId : undefined}
      onChange={(groupId) => {
        if (isAdmin) {
          updateUrl(groupId);
        }
        groupsFilterSectionData.onChange([groupId]);
      }}
      popupMatchSelectWidth={false}
      styles={{
        popup: {
          root: {
            maxWidth: maxDropdownWidth,
          },
        },
      }}
      optionLabelProp="label"
      size={size ?? "large"}
      optionFilterProp="searchValue"
      options={parsedOptions}
      mobileTitle="Group"
      $singleRow={isBottomNavigator}
      $isBottomNavigator={isBottomNavigator}
      hideArrow={isBottomNavigator}
    />
  );
};

export default GroupSelect;
